import _ from 'lodash';
import Helpers from '@/shared/helpers/functions';
import States from '@/resources/models/states';

export default {
  getActiveReportOfSicknessForEmployee (employee, reportsOfSickness) {
    if (!reportsOfSickness.hasOwnProperty(employee.id)) return undefined;
    return reportsOfSickness[employee.id].find(r => r.recovery == null && r.status !== States.Removed);
  },

  getActiveReportOfSickness (reportsOfSickness) {
    if (reportsOfSickness == null) return [];
    return reportsOfSickness.filter(r => r.recovery == null && r.status !== States.Removed);
  },

  getPolicyType (policies) {
    return _.uniq(policies.map(p => p.productType));
  },

  /* Find the actual Policies from various Participations
     Use this composition to find active or any.
     supplying function with only active participation and active policies
     will filter only eligible policies */
  getPoliciesFromParticipations (participations, policies) {
    let participationPolicyNumbers = participations.map(p => p.policyNumber);
    return policies.filter(policy => participationPolicyNumbers.indexOf(policy.policyNumber) > -1);
  },

  getPolicyFromParticipation (participation, policies) {
    return policies.find(policy => policy.policyNumber === participation.policyNumber);
  },

  getPoliciesFromParticipationsWithCoverageOnDate (participations, policies, date) {
    let participationsWithCoverage = participations
      .filter(p => p.isActiveOnDate(date) && !p.isActiveWaiverOnDate(date));
    let policiesWithCoverage = policies
      .filter(p => p.isActiveOnDate(date));
    return this.getPoliciesFromParticipations(participationsWithCoverage, policiesWithCoverage);
  },

  getPoliciesFromParticipationsWithoutCoverageOnDate (participations, policies, date) {
    let participationsWithoutCoverage = participations
      .filter(p => !p.isActiveOnDate(date) || p.isActiveWaiverOnDate(date));
    let policiesWithoutCoverage = policies
      .filter(p => !p.isActiveOnDate(date));
    return this.getPoliciesFromParticipations(participationsWithoutCoverage, policiesWithoutCoverage);
  },

  /* Get Policies for Employee based on their participation,
     only gets active policies with active participations */
  getEmployeePolicies (employee, policies) {
    if (employee == null || policies == null) return [];
    return this.getPoliciesFromParticipations(employee.participations, policies);
  },

  getEmployeePolicyTypes (employee, policies) {
    return this.getPolicyType(this.getEmployeePolicies(employee, policies));
  },

  /* Get most recent details */
  getLatestDetailsFromEmploymentRelationship (relationship) {
    // first filter those without untilDate, only then which do have
    let details = relationship.employmentRelationshipDetails
      .filter(d => d.untilDate == null);

    if (details.length === 0) { details = relationship.employmentRelationshipDetails; }

    if (details.length > 0) {
      let current = details[0];
      for (let d in details) {
        if (Helpers.Dates.isGreaterThan(d.fromDate, current.fromDate)) {
          current = d;
        }
      }
      return current;
    } else {
      return null;
    }
  },

  getLatestReportOfSickness (reportsOfSickness) {
    if (reportsOfSickness != null && reportsOfSickness.length > 0) {
      let current = reportsOfSickness[0];
      for (let r in reportsOfSickness) {
        if (new Date(this.getLatestDateFromReportOfSickness(r)) > new Date(this.getLatestDateFromReportOfSickness(current))) {
          current = r;
        }
      }
      return current;
    }
    return null;
  },

  getLatestDateFromReportOfSickness (reportOfSickness) {
    if (reportOfSickness == null) return null;

    if (reportOfSickness.recovery != null) {
      return reportOfSickness.recovery.recoveryDate;
    }

    if (reportOfSickness.periods.length > 0) {
      return reportOfSickness.periods
        .map(p => p.startDate).sort().reverse()[0];
    }
    return reportOfSickness.firstOfAbsence;
  },

  getSecondLastDateFromReportOfSickness (reportOfSickness) {
    if (reportOfSickness == null) return null;

    let dateToSkip = this.getLatestDateFromReportOfSickness(reportOfSickness);
    if (reportOfSickness.periods.length > 0) {
      let periods = Object.assign([], reportOfSickness.periods).reverse();
      for (let p of periods) {
        if (p.startDate !== dateToSkip) {
          return p.startDate;
        }
      }
    }
    if (reportOfSickness.firstOfAbsence !== dateToSkip) return reportOfSickness.firstOfAbsence;
    return reportOfSickness.firstOfAbsence;
  },

  getLatestDisabilityRateFromReportOfSickness (reportOfSickness) {
    if (reportOfSickness.recovery) return 0.0;
    if (reportOfSickness.periods.length > 0) return reportOfSickness.periods[reportOfSickness.periods.length - 1].disabilityRate;
    return reportOfSickness.disabilityRate;
  },

  // check if the new first of absence is within 28 days of the last report of sickness
  isFirstOfAbsenceWithinBufferLastReport (reportsOfSickness, firstOfAbsence) {
    if (reportsOfSickness == null) return false;
    if (_.some(reportsOfSickness, r => r.recovery == null)) return false;

    const latestRecoveryDate = reportsOfSickness
      .filter(r => r.recovery != null)
      .map(r => r.recovery.recoveryDate)
      .sort().reverse()[0];

    return Helpers.Dates.differenceBetweenDatesInDays(latestRecoveryDate, firstOfAbsence) <= 28;
  },

  getLatestIndemnityProcessedDate (reportOfSickness) {
    let result = reportOfSickness.processedUntilDate;
    reportOfSickness.periods.forEach(p => {
      if (p.processedUntilDate) {
        if (new Date(p.processedUntilDate) > new Date(result)) {
          result = p.processedUntilDate;
        }
      }
    });
    return result;
  }
};
